import { useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { message } from '../utils/antdMessage';
import { apiFetch } from '../utils/api';
import { buildPickPointShareUrl } from '../utils/pickPointCapture';
import { normalizeRescuePushSettings } from '../utils/rescuePushSettings';
import siteConfig from '../config/site-config';

/**
 * App 绑定令牌 / 手机选点 / 下达指令
 */
export default function useRescuePushBind() {
  const [trackerForm, setTrackerForm] = useState({
    name: '',
    group_name: '',
    org_name: '',
    note: '',
    ttl_days: 30,
    custom_token: ''
  });
  const [trackerTokens, setTrackerTokens] = useState([]);
  const [trackerLoading, setTrackerLoading] = useState(false);
  const trackerTtlFallbackRef = useRef(30);
  const pickPointTtlFallbackRef = useRef(30);
  const [appCommandForm, setAppCommandForm] = useState({
    text: '',
    important: false,
    to_all: true,
    selectedTokens: []
  });
  const [appCommandLoading, setAppCommandLoading] = useState(false);
  const [pickPointForm, setPickPointForm] = useState({ note: '', ttl_minutes: 30 });
  const [pickPointTokens, setPickPointTokens] = useState([]);
  const [pickPointLoading, setPickPointLoading] = useState(false);

  useEffect(() => {
    fetch(`${siteConfig.getApiUrl()}/admin/ui-settings/public`)
      .then((response) => response.json())
      .then((data) => {
        const rescue = normalizeRescuePushSettings(data?.settings?.rescuePushSettings);
        setPickPointForm((form) => ({ ...form, ttl_minutes: rescue.pickPointTtlMinutes }));
        setTrackerForm((form) => ({ ...form, ttl_days: rescue.trackerTtlDays }));
        trackerTtlFallbackRef.current = rescue.trackerTtlDays;
        pickPointTtlFallbackRef.current = rescue.pickPointTtlMinutes;
      })
      .catch(() => {});
  }, []);

  const fetchTrackerTokens = async () => {
    try {
      const response = await apiFetch('/api/tracker/list');
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || '获取绑定令牌失败');
      setTrackerTokens(data.tokens || []);
    } catch (error) {
      console.error('获取 App 绑定令牌失败:', error);
    }
  };

  const fetchPickPointTokens = async () => {
    try {
      // 推送页使用 recent=1 只获取 24 小时内的记录
      const response = await apiFetch('/api/pick-point/list?recent=1');
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || '获取手机选点链接失败');
      
      // 合并 sessionStorage 中本次会话创建的记录
      const sessionTokens = getSessionPickPointTokens();
      const recentTokens = data.tokens || [];
      
      // 去重合并（优先使用最新数据）
      const tokenMap = new Map();
      sessionTokens.forEach((token) => tokenMap.set(token.id, token));
      recentTokens.forEach((token) => tokenMap.set(token.id, token));
      
      setPickPointTokens(Array.from(tokenMap.values()).sort((a, b) => {
        const aTime = new Date(a.used_at || a.created_at).getTime();
        const bTime = new Date(b.used_at || b.created_at).getTime();
        return bTime - aTime;
      }));
    } catch (error) {
      console.error('获取手机选点链接失败:', error);
    }
  };

  // sessionStorage 键名
  const SESSION_KEY = 'rescue_push_pick_point_session_tokens';

  // 获取本次会话创建的记录
  const getSessionPickPointTokens = () => {
    try {
      const stored = sessionStorage.getItem(SESSION_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  // 保存本次会话创建的记录
  const saveSessionPickPointToken = (token) => {
    try {
      const existing = getSessionPickPointTokens();
      // 避免重复
      const filtered = existing.filter((t) => t.id !== token.id);
      filtered.unshift(token);
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(filtered));
    } catch (error) {
      console.warn('保存会话记录失败:', error);
    }
  };

  // 从会话记录中删除
  const removeSessionPickPointToken = (id) => {
    try {
      const existing = getSessionPickPointTokens();
      const filtered = existing.filter((t) => t.id !== id);
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(filtered));
    } catch (error) {
      console.warn('删除会话记录失败:', error);
    }
  };

  useEffect(() => {
    fetchTrackerTokens();
    fetchPickPointTokens();
  }, []);

  const copyPlainText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.left = '-9999px';
    document.body.appendChild(area);
    area.select();
    document.execCommand('copy');
    document.body.removeChild(area);
  };

  const copyPickPointLink = async (item) => {
    const token = typeof item === 'string' ? item : item?.token;
    const shortCode = typeof item === 'string' ? '' : item?.short_code;
    const link = buildPickPointShareUrl(window.location.origin, shortCode, token);
    try {
      await copyPlainText(link);
      message.success(shortCode ? '选点短链已复制，可直接发微信' : '手机选点链接已复制');
    } catch (error) {
      message.error('复制失败，请手动复制');
    }
  };

  const createPickPointToken = async () => {
    setPickPointLoading(true);
    try {
      const response = await apiFetch('/api/pick-point/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pickPointForm)
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || '生成手机选点链接失败');
      
      // 保存到 sessionStorage（本次会话创建的记录）
      if (data.token) {
        saveSessionPickPointToken(data.token);
      }
      
      setPickPointForm({ note: '', ttl_minutes: pickPointTtlFallbackRef.current, icon_id: null });
      await fetchPickPointTokens();
      await copyPickPointLink(data.token);
    } catch (error) {
      message.error(error.message);
    } finally {
      setPickPointLoading(false);
    }
  };

  const revokePickPointToken = async (token) => {
    try {
      const response = await apiFetch('/api/pick-point/revoke', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || '撤销失败');
      
      // 从 sessionStorage 中移除（如果存在）
      if (data.token?.id) {
        removeSessionPickPointToken(data.token.id);
      }
      
      await fetchPickPointTokens();
      message.success('手机选点链接已撤销');
    } catch (error) {
      message.error(error.message);
    }
  };

  const confirmPickPointToken = async (token) => {
    try {
      const response = await apiFetch('/api/pick-point/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || '确认失败');
      
      await fetchPickPointTokens();
      message.success('已确认位置上报');
    } catch (error) {
      message.error(error.message);
    }
  };

  const deletePickPointToken = async (id) => {
    try {
      const response = await apiFetch(`/api/pick-point/${encodeURIComponent(id)}`, {
        method: 'DELETE'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) throw new Error(data.error || '删除失败');
      
      // 从 sessionStorage 中移除
      removeSessionPickPointToken(id);
      
      await fetchPickPointTokens();
      message.success('已删除手机选点');
    } catch (error) {
      message.error(error.message || '删除失败');
    }
  };

  const copyTrackerToken = async (token) => {
    try {
      await copyPlainText(String(token || ''));
      message.success('绑定令牌已复制');
    } catch (error) {
      message.error('复制失败，请手动复制');
    }
  };

  const handleTrackerTtlDaysChange = (event) => {
    const raw = event.target.value;
    const next = Number.parseInt(String(raw), 10);
    if (next === 0) {
      Modal.confirm({
        title: '确认长期有效？',
        content: '有效期设置为 0 天将视为长期有效，是否确认？',
        okText: '确认',
        cancelText: '取消',
        centered: true,
        onOk: () => {
          setTrackerForm((form) => ({ ...form, ttl_days: 0 }));
        },
        onCancel: () => {
          const fallback = trackerTtlFallbackRef.current > 0 ? trackerTtlFallbackRef.current : 30;
          setTrackerForm((form) => ({ ...form, ttl_days: fallback }));
        }
      });
      return;
    }
    if (Number.isFinite(next) && next > 0) {
      trackerTtlFallbackRef.current = next;
    }
    setTrackerForm((form) => ({ ...form, ttl_days: raw }));
  };

  const assignStreamKey = async ({ deviceUniqueId, name, force = false } = {}) => {
    const body = {
      device_unique_id: deviceUniqueId || undefined,
      name: name || undefined,
      force: !!force
    };
    const response = await apiFetch('/api/tracker/assign-short-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));
    if (response.status === 409 && data.code === 'needs_confirm') {
      const ok = window.confirm(data.error || '该队员已有推流码，重新获取将作废旧码，是否继续？');
      if (!ok) {
        return { cancelled: true, token: data.token, stream_key: data.stream_key };
      }
      return assignStreamKey({ deviceUniqueId, name, force: true });
    }
    if (!response.ok || !data.success) {
      throw new Error(data.error || '获取推流码失败');
    }
    await fetchTrackerTokens();
    const sk = String(data.stream_key || data.token?.token || data.token?.stream_key || '').trim();
    if (sk) {
      try {
        await navigator.clipboard.writeText(sk);
        message.success(data.regenerated ? `已重新获取并复制：${sk}` : `已获取并复制：${sk}`);
      } catch (_) {
        message.success(data.regenerated ? `已重新获取：${sk}` : `已获取：${sk}`);
      }
    } else {
      message.success('已获取推流码');
    }
    return data;
  };

  const createTrackerToken = async () => {
    const ttlDays = Number.parseInt(String(trackerForm.ttl_days), 10);
    if (ttlDays === 0) {
      const confirmed = await new Promise((resolve) => {
        Modal.confirm({
          title: '确认长期有效？',
          content: '有效期设置为 0 天将视为长期有效，是否确认？',
          okText: '确认',
          cancelText: '取消',
          centered: true,
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        });
      });
      if (!confirmed) return;
    }
    if (!trackerForm.name.trim() || !trackerForm.group_name.trim()) {
      message.error('请填写姓名和小组');
      return;
    }
    setTrackerLoading(true);
    try {
      const response = await apiFetch('/api/tracker/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trackerForm)
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || '生成绑定令牌失败');
      setTrackerForm({ name: '', group_name: '', org_name: '', note: '', ttl_days: trackerTtlFallbackRef.current, custom_token: '' });
      await fetchTrackerTokens();
      await copyTrackerToken(data.token.token);
      message.success(ttlDays === 0 ? '长期有效绑定令牌已生成，令牌已复制' : '绑定令牌已生成并复制');
    } catch (error) {
      message.error(error.message);
    } finally {
      setTrackerLoading(false);
    }
  };

  const sendAppCommand = async () => {
    const text = String(appCommandForm.text || '').trim();
    if (!text) {
      message.warning('请输入指令内容');
      return;
    }
    if (!appCommandForm.to_all && (!appCommandForm.selectedTokens || appCommandForm.selectedTokens.length === 0)) {
      message.warning('请勾选接收人，或选择「全部有效 App」');
      return;
    }
    setAppCommandLoading(true);
    try {
      const response = await apiFetch('/api/tracker/commands', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          important: !!appCommandForm.important,
          to_all: !!appCommandForm.to_all,
          tokens: appCommandForm.to_all ? undefined : appCommandForm.selectedTokens
        })
      });
      const raw = await response.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch (_) {
        throw new Error('下达接口未生效（返回了网页而非 JSON）。请重启后端后再试');
      }
      if (!response.ok || !data.success) throw new Error(data.error || '下达失败');
      message.success(`已下达给 ${data.count} 台 App${data.important ? '（重要）' : ''}`);
      setAppCommandForm((form) => ({ ...form, text: '' }));
    } catch (error) {
      message.error(error.message || '下达指令失败');
    } finally {
      setAppCommandLoading(false);
    }
  };

  const toggleAppCommandRecipient = (token) => {
    setAppCommandForm((form) => {
      const set = new Set(form.selectedTokens || []);
      if (set.has(token)) set.delete(token);
      else set.add(token);
      return { ...form, selectedTokens: Array.from(set), to_all: false };
    });
  };

  const updateTrackerMode = async (token, trackMode) => {
    try {
      const response = await apiFetch('/api/tracker/set-mode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, track_mode: trackMode })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || '更新模式失败');
      await fetchTrackerTokens();
      message.success(trackMode === 'boosted' ? '已远程加强追踪' : '已切换为省电追踪');
    } catch (error) {
      message.error(error.message);
    }
  };

  const revokeTrackerToken = async (token) => {
    if (!window.confirm('撤销后该令牌将删除，App 无法继续上报，确定吗？')) return;
    try {
      const response = await apiFetch('/api/tracker/revoke', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || '撤销失败');
      setTrackerTokens((list) => list.filter((item) => item.token !== token));
      message.success('绑定令牌已删除');
    } catch (error) {
      message.error(error.message);
    }
  };

  return {
    trackerForm,
    setTrackerForm,
    trackerTokens,
    trackerLoading,
    appCommandForm,
    setAppCommandForm,
    appCommandLoading,
    pickPointForm,
    setPickPointForm,
    pickPointTokens,
    setPickPointTokens,
    pickPointLoading,
    fetchTrackerTokens,
    fetchPickPointTokens,
    copyPickPointLink,
    createPickPointToken,
    revokePickPointToken,
    confirmPickPointToken,
    deletePickPointToken,
    copyTrackerLink: copyTrackerToken,
    handleTrackerTtlDaysChange,
    createTrackerToken,
    assignStreamKey,
    sendAppCommand,
    toggleAppCommandRecipient,
    updateTrackerMode,
    revokeTrackerToken
  };
}
